import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getAdminBranding,
    getPublicBranding,
    updateBrandingText,
    updateBrandingContact,
    uploadBrandingLogo,
    deleteBrandingLogo,
    brandingLogoUrl,
} from '../../api/branding';
import { useBranding } from '../../context/BrandingContext';
import appConfig from '../../config/appConfig';
import { showConfirm } from '../../utils/popup';
import Button from '../../components/common/Button';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { isUpgradeRequiredError, apiErrorMessage } from '../../utils/apiError';
import { useRegionalFormDefaults } from '../../context/RegionalSettingsContext';
import { TextInput } from '../../components/common/form';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';

export default function BrandingSettings() {
    const { t } = useTranslation();
    const { refresh: refreshBranding } = useBranding();
    const regional = useRegionalFormDefaults();
    const fileInputRef = useRef(null);

    const [appName, setAppName] = useState('');
    const [tagline, setTagline] = useState('');
    const [hasLogo, setHasLogo] = useState(false);
    const [preview, setPreview] = useState(null);

    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [removingLogo, setRemovingLogo] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [enterpriseLocked, setEnterpriseLocked] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [enterpriseMessage, setEnterpriseMessage] = useState('');
    const [supportEmail, setSupportEmail] = useState('');
    const [supportPhone, setSupportPhone] = useState('');
    const [brandingReady, setBrandingReady] = useState(false);

        const flash = (msg, isError = false) => {
        if (isError) {
            setErrorMsg(msg);
            setSuccessMsg('');
            return;
        }
        setSuccessMsg(msg);
        setErrorMsg('');
        setTimeout(() => { setSuccessMsg(''); }, 4000);
    };

    useEffect(() => {
        getPublicBranding()
            .then((data) => {
                setSupportEmail(data.supportEmail || '');
                setSupportPhone(data.supportPhone || '');
            })
            .catch(() => {});

        getAdminBranding()
            .then((data) => {
                setEnterpriseLocked(false);
                setAppName(data.appName || '');
                setTagline(data.tagline || '');
                setHasLogo(!!data.hasLogo);
                setSupportEmail(data.supportEmail || '');
                setSupportPhone(data.supportPhone || '');
                setBrandingReady(true);
            })
            .catch((err) => {
                if (isUpgradeRequiredError(err)) {
                    setEnterpriseLocked(true);
                    setEnterpriseMessage(
                        typeof err?.response?.data?.details === 'string'
                            ? err.response.data.details
                            : t('settings.license.enterpriseOnly')
                    );
                    setBrandingReady(true);
                    return;
                }
                setLoadFailed(true);
                setErrorMsg(t('settings.branding.loadFailed'));
                setBrandingReady(true);
            });
    }, [t]);

    const dirtyValue = brandingReady && !loadFailed ? {
        appName,
        tagline,
        supportEmail,
        supportPhone,
        selectedFileName: selectedFile?.name || null,
        selectedFileSize: selectedFile?.size || null,
    } : null;

    const persistBranding = async () => {
        await updateBrandingContact(supportPhone || null, supportEmail || null);
        if (!enterpriseLocked) {
            await updateBrandingText(appName || null, tagline || null);
            if (selectedFile) {
                await uploadBrandingLogo(selectedFile);
                setHasLogo(true);
                setSelectedFile(null);
                setPreview(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        }
        await refreshBranding(selectedFile ? { bustLogo: true } : undefined);
        return {
            appName,
            tagline,
            supportEmail,
            supportPhone,
            selectedFileName: null,
            selectedFileSize: null,
        };
    };

    const { markSaved } = useUnsavedChanges(dirtyValue, {
        enabled: brandingReady && !loadFailed,
        onSave: async () => { await persistBranding(); },
    });

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const next = await persistBranding();
            markSaved(next);
            flash(t('settings.branding.saveSuccess'));
        } catch (err) {
            flash(apiErrorMessage(err, t('settings.branding.saveFailed')), true);
        } finally {
            setSaving(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            flash(t('settings.branding.validImageFile'), true);
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            flash(t('settings.branding.logoTooLargeAlert'), true);
            return;
        }
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
    };

    const handleUploadLogo = async () => {
        if (!selectedFile) return;
        setUploading(true);
        try {
            await uploadBrandingLogo(selectedFile);
            await refreshBranding({ bustLogo: true });
            setHasLogo(true);
            setSelectedFile(null);
            setPreview(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            markSaved({
                appName,
                tagline,
                supportEmail,
                supportPhone,
                selectedFileName: null,
                selectedFileSize: null,
            });
            flash(t('settings.branding.logoUploadSuccess'));
        } catch (err) {
            flash(apiErrorMessage(err, t('settings.branding.logoUploadFailed')), true);
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveLogo = async () => {
        if (!await showConfirm(t('settings.branding.removeLogoConfirm'))) return;
        setRemovingLogo(true);
        try {
            await deleteBrandingLogo();
            await refreshBranding();
            setHasLogo(false);
            setPreview(null);
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            flash(t('settings.branding.logoRemoved'));
        } catch {
            flash(t('settings.branding.logoRemoveFailed'), true);
        } finally {
            setRemovingLogo(false);
        }
    };

    const liveLogoSrc = preview
        ? preview
        : hasLogo
            ? `${brandingLogoUrl()}?t=${Date.now()}`
            : appConfig.logoPath;

    const displayName = appName || appConfig.appName;
    const displayTag = tagline || appConfig.tagline;

    if (!brandingReady) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={t('settings.branding.title')}
                subtitle={t('settings.branding.loadingSubtitle')}
            />
        );
    }

    if (loadFailed) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    eyebrow={t('nav.system')}
                    title={t('settings.branding.title')}
                    subtitle={t('settings.branding.loadingSubtitle')}
                />
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {errorMsg || t('settings.branding.loadFailed')}
                </div>
                <div className="mt-4">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => window.location.reload()}
                    >
                        {t('common.retry', { defaultValue: 'Retry' })}
                    </Button>
                </div>
            </div>
        );
    }

    const alertBanners = (
        <>
            {successMsg && (
                <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    {successMsg}
                </div>
            )}
            {errorMsg && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {errorMsg}
                </div>
            )}
        </>
    );

    if (enterpriseLocked) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    eyebrow={t('nav.system')}
                    title={t('settings.branding.title')}
                    subtitle={t('settings.branding.enterpriseSubtitle')}
                />

                <form onSubmit={handleSave} className="space-y-6">
                    {alertBanners}

                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <p className="font-semibold">{t('settings.branding.enterpriseFeature')}</p>
                        <p className="mt-2 leading-6">{enterpriseMessage}</p>
                        <p className="mt-3 rounded-lg border border-amber-200 bg-white px-3 py-2 text-slate-700">
                            {t('settings.branding.enterpriseNote')}
                        </p>
                    </div>

                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                                {t('settings.branding.stepOne')}
                            </p>
                            <h2 className="mt-1 text-lg font-semibold text-slate-900">
                                {t('settings.branding.academyContact')}
                            </h2>
                            <p className="mt-1 max-w-2xl text-sm text-slate-600">
                                {t('settings.branding.contactNote')}
                            </p>
                        </div>
                        <div className="grid max-w-3xl gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
                            <TextInput
                                label={t('settings.branding.supportEmail')}
                                type="email"
                                value={supportEmail}
                                onChange={(e) => setSupportEmail(e.target.value)}
                                placeholder="support@youracademy.com"
                            />
                            <TextInput
                                label={t('settings.branding.supportPhone')}
                                type="tel"
                                value={supportPhone}
                                onChange={(e) => setSupportPhone(e.target.value)}
                                placeholder={regional.phoneFormatHint || `e.g. ${regional.phoneDialCode} ${regional.phoneLocalPlaceholder}`}
                            />
                        </div>
                    </section>

                    <div className="sticky bottom-4 z-10 flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
                        >
                            {saving ? t('forms.common.saving') : t('settings.branding.saveSettings')}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                eyebrow={t('nav.system')}
                title={t('settings.branding.title')}
                subtitle={t('settings.branding.subtitle')}
            />

            <form onSubmit={handleSave} className="space-y-6">
                {alertBanners}

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.branding.stepOne')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.branding.appNameTagline')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.branding.sectionIdentityDesc')}
                        </p>
                    </div>
                    <div className="grid max-w-3xl gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
                        <TextInput
                            label={t('settings.branding.appName')}
                            type="text"
                            placeholder={appConfig.appName}
                            value={appName}
                            maxLength={150}
                            onChange={(e) => setAppName(e.target.value)}
                            note={t('settings.branding.appNameNote', { default: appConfig.appName })}
                        />
                        <TextInput
                            label={t('settings.branding.tagline')}
                            type="text"
                            placeholder={appConfig.tagline}
                            value={tagline}
                            maxLength={255}
                            onChange={(e) => setTagline(e.target.value)}
                            note={t('settings.branding.taglineNote')}
                        />
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.branding.stepTwo')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.branding.logoImage')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.branding.sectionLogoDesc')}
                        </p>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <div
                            className="flex max-w-md cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center transition hover:border-primary-400 hover:bg-primary-50/40"
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                        >
                            {selectedFile ? (
                                <>
                                    <img src={preview} alt={t('setupWizard.step1.logoPreviewAlt', { defaultValue: 'Logo preview' })} className="h-20 w-20 rounded-xl object-contain shadow" />
                                    <p className="text-sm font-medium text-slate-700">{selectedFile.name}</p>
                                    <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                                </>
                            ) : (
                                <>
                                    <svg className="h-10 w-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-medium text-slate-700">{t('settings.branding.clickChooseLogo')}</p>
                                        <p className="mt-1 text-xs text-slate-500">{t('settings.branding.logoHint')}</p>
                                    </div>
                                </>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                        />

                        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                            <div>
                                {hasLogo && (
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        disabled={removingLogo}
                                        onClick={handleRemoveLogo}
                                    >
                                        {removingLogo ? t('settings.branding.removing') : t('settings.branding.removeLogo')}
                                    </Button>
                                )}
                            </div>
                            <Button
                                type="button"
                                variant="secondary"
                                disabled={!selectedFile || uploading}
                                onClick={handleUploadLogo}
                            >
                                {uploading ? t('settings.branding.uploading') : t('settings.branding.uploadLogo')}
                            </Button>
                        </div>

                        <p className="text-sm text-slate-500">
                            {hasLogo
                                ? t('settings.branding.logoActive')
                                : t('settings.branding.logoDefault')}
                        </p>
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.branding.stepThree')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.branding.academyContact')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.branding.contactNote')}
                        </p>
                    </div>
                    <div className="grid max-w-3xl gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
                        <TextInput
                            label={t('settings.branding.supportEmail')}
                            type="email"
                            value={supportEmail}
                            onChange={(e) => setSupportEmail(e.target.value)}
                            placeholder="support@youracademy.com"
                        />
                        <TextInput
                            label={t('settings.branding.supportPhone')}
                            type="tel"
                            value={supportPhone}
                            onChange={(e) => setSupportPhone(e.target.value)}
                            placeholder={regional.phoneFormatHint || `e.g. ${regional.phoneDialCode} ${regional.phoneLocalPlaceholder}`}
                        />
                    </div>
                </section>

                <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                            {t('settings.branding.stepFour')}
                        </p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">
                            {t('settings.branding.livePreview')}
                        </h2>
                        <p className="mt-1 max-w-2xl text-sm text-slate-600">
                            {t('settings.branding.previewNote')}
                        </p>
                    </div>
                    <div className="space-y-4 px-5 py-5 sm:px-6">
                        <div className="flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 shadow-lg">
                            <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-white/95 p-1.5 shadow">
                                <img
                                    src={liveLogoSrc}
                                    alt={displayName}
                                    className="h-full w-full rounded-lg object-contain"
                                    onError={(e) => { e.currentTarget.src = appConfig.logoPath; }}
                                />
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-bold text-white">{displayName}</p>
                                <p className="truncate text-xs text-slate-400">{displayTag}</p>
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-primary-50 to-white p-6 text-center">
                            <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-2 shadow-lg">
                                <img
                                    src={liveLogoSrc}
                                    alt={displayName}
                                    className="h-full w-full rounded-xl object-contain"
                                    onError={(e) => { e.currentTarget.src = appConfig.logoPath; }}
                                />
                            </div>
                            <p className="text-base font-bold text-slate-900">{displayName}</p>
                            <p className="mt-0.5 text-xs text-slate-500">{t('settings.branding.loginPreview')}</p>
                        </div>

                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                            <p className="font-semibold">{t('settings.branding.instantNote')}</p>
                            <p className="mt-1 text-xs leading-relaxed">{t('settings.branding.instantDesc')}</p>
                        </div>
                    </div>
                </section>

                <div className="sticky bottom-4 z-10 flex justify-end">
                    <button
                        type="submit"
                        disabled={saving}
                        className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                        {saving ? t('forms.common.saving') : t('settings.branding.saveSettings')}
                    </button>
                </div>
            </form>
        </div>
    );
}
